import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { supabase } from "@/lib/supabase";
import LoadingSpinner from "@/components/LoadingSpinner";
import SEO from '@/components/SEO';
import { ArrowLeft } from "lucide-react";

export default function ViewCatalog() {
  const [, params] = useRoute("/ver-catalogo/:slug");
  const slug = params?.slug;
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const searchParams = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const fileParam = searchParams.get('file');

  useEffect(() => {
    async function getPublicPdfUrl() {
      setLoading(true);
      setError(null);
      try {
        if (!fileParam) {
          throw new Error("No se proporcionó ningún archivo PDF");
        }

        const { data: urlData } = supabase.storage
          .from('pdfs')
          .getPublicUrl(fileParam);

        if (!urlData.publicUrl) {
          throw new Error("No se pudo obtener la URL pública del PDF.");
        }

        // Simplemente establecemos la URL pública
        setPdfUrl(urlData.publicUrl);

      } catch (err: any) {
        console.error("Error al obtener la URL del PDF:", err);
        setError(err.message || "Ocurrió un error inesperado");
      } finally {
        setLoading(false);
      }
    }

    getPublicPdfUrl();
  }, [fileParam]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0A0A0B] flex items-center justify-center z-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex flex-col items-center justify-center p-4">
        <SEO title="Error al cargar Catálogo" description={error} />
        <h1 className="text-2xl font-bold text-white mb-4">Error al cargar el catálogo</h1>
        <p className="text-zinc-400 mb-6 text-center">
          {error}
        </p>
        <a href="/catalogos" className="px-4 py-2 bg-[#FCAA0D] text-white rounded-lg font-medium">
          Volver a catálogos
        </a>
      </div>
    );
  }

  if (pdfUrl) {
    const pageTitle = slug
      ?.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || "Catálogo";

    return (
      <div className="fixed inset-0 w-full h-full bg-black">
        <SEO
          title={`${pageTitle} | Innotec Food Equipment`}
          description={`Visualiza el catálogo ${pageTitle}.`}
          canonicalUrl={`/ver-catalogo/${slug}`}
        />
        <object
          data={pdfUrl}
          type="application/pdf"
          className="w-full h-full border-none"
        >
          <div className="flex flex-col items-center justify-center h-full text-white bg-black p-4">
              <p className="mb-4 text-center">Tu navegador no puede mostrar el PDF directamente.</p>
              <a href={pdfUrl} download className="px-4 py-2 bg-[#FCAA0D] text-black rounded-lg font-medium">
                  Descargar PDF
              </a>
          </div>
        </object>
        <a href="/catalogos" className="fixed top-4 left-4 z-10 flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-full backdrop-blur-sm hover:bg-black/80 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Volver</span>
        </a>
      </div>
    );
  }

  return null;
}
